import React from 'react';
import PropTypes from 'prop-types';
import { surfaces, borders } from '../../tokens';

/**
 * Panel Component
 * 
 * A versatile panel component for grouping content.
 * Similar to Card but more generic and flexible for various layouts.
 * Commonly used in settings pages, edit panels, and content sections.
 * 
 * @example
 * // Basic panel
 * <Panel>
 *   <h3>Panel Title</h3>
 *   <p>Panel content</p>
 * </Panel>
 * 
 * @example
 * // Panel with border and shadow
 * <Panel border shadow padding="lg">
 *   <div>Content with border and shadow</div>
 * </Panel>
 * 
 * @example
 * // Panel with colored background
 * <Panel background="gray" rounded="lg">
 *   <div>Colored panel</div>
 * </Panel>
 */
const Panel = ({
  children,
  background = 'white',
  border = true,
  shadow = false,
  rounded = 'md',
  padding = 'md',
  className = '',
  as: Component = 'div',
  ...props
}) => {
  // Background classes
  const backgroundClasses = {
    transparent: '',
    // Theme-aware surfaces
    white: surfaces.primary,
    gray: surfaces.secondary,
    // Light: subtle blue tint, Dark: subtle blue tint (still readable on dark)
    blue: 'bg-blue-50 dark:bg-blue-900/20',
    glass: surfaces.glassCard,
  };
  
  // Border classes
  const borderClasses = border ? `border ${background === 'glass' ? borders.glass : borders.default}` : '';
  
  // Shadow classes
  const shadowClasses = shadow ? 'shadow-md' : '';
  
  // Rounded classes
  const roundedClasses = {
    none: '',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
  };
  
  // Padding classes
  const paddingClasses = {
    none: '',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
    xl: 'p-8',
  };
  
  // Combine all classes
  const combinedClasses = [
    backgroundClasses[background],
    borderClasses,
    shadowClasses,
    roundedClasses[rounded],
    paddingClasses[padding],
    className,
  ].filter(Boolean).join(' ');
  
  return (
    <Component className={combinedClasses} {...props}>
      {children}
    </Component>
  );
};

Panel.propTypes = {
  /** Panel content */
  children: PropTypes.node.isRequired,
  /** Background color variant */
  background: PropTypes.oneOf(['transparent', 'white', 'gray', 'blue', 'glass']),
  /** Whether to show border */
  border: PropTypes.bool,
  /** Whether to show shadow */
  shadow: PropTypes.bool,
  /** Border radius size */
  rounded: PropTypes.oneOf(['none', 'sm', 'md', 'lg', 'xl', '2xl']),
  /** Internal padding size */
  padding: PropTypes.oneOf(['none', 'sm', 'md', 'lg', 'xl']),
  /** Additional CSS classes */
  className: PropTypes.string,
  /** HTML element or React component to render as */
  as: PropTypes.elementType,
};

export default Panel;
